import { Button, Flex, Form, Input, Popover, Spin, Skeleton } from 'antd';
import React, { useState, useEffect } from 'react';
import { useDebounce } from 'use-debounce';
import {
  ArrowsRightLeftIcon,
  ArrowRightIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import {
  isCryptoCurrency,
  NetworkLogoMap,
  upperCaseNetworkType,
} from 'features/bill/lib';
import { US, EU } from 'country-flag-icons/react/1x1';
import { CurrencySelect } from './CurrencySelect';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useDestinationCapabilities,
  useSourceCapabilities,
  useTransferQuote,
} from './useTransferCapabilities';

const { Item } = Form;

interface TransferFormData {
  sourceCurrency: string;
  sourceCurrencySymbol: string;
  sourceNetwork: string;
  sourceAmount: string;
  destinationCurrency: string;
  destinationCurrencySymbol: string;
  destinationNetwork: string;
  destinationAmount: string;
}

const SelectedCurrencyNetwork: React.FC<{
  currency: string;
  network: string;
}> = ({ currency, network }) => {
  const renderCurrencyImage = (): React.ReactNode => {
    let src;
    if (isCryptoCurrency(currency)) {
      src = NetworkLogoMap[upperCaseNetworkType(network)];
    } else if (currency === 'usd') {
      return <US className="w-4 rounded-full" />;
    } else if (currency === 'eur') {
      return <EU className="w-4 rounded-full" />;
    }

    return (
      <div className="h-4 w-4">
        <img src={src} alt="" aria-hidden="true" />
      </div>
    );
  };

  const renderCurrencyLabel = (): React.ReactNode => {
    let label;
    if (isCryptoCurrency(currency)) {
      label = (
        <span className="flex items-center">
          {currency?.toUpperCase()}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-3">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m9 20.247 6-16.5"
            />
          </svg>
          {upperCaseNetworkType(network)}
        </span>
      );
    } else {
      label = currency?.toUpperCase();
    }

    return <span>{label}</span>;
  };

  return (
    <Flex
      align="center"
      gap={'small'}
      className="text-slate-400 flex cursor-pointer hover:text-slate-200 transition-colors duration-200">
      {renderCurrencyImage()}
      {renderCurrencyLabel()}
      <ChevronUpDownIcon className="w-4" />
    </Flex>
  );
};

const getCurrencyTypeByNetworkAndSymbol = (
  currenciesByNetworks: Record<string, { symbol: string; type: string }[]>,
  network: string,
  currencySymbol: string
) => {
  return currenciesByNetworks?.[network]?.find(
    (curObj) => curObj.symbol === currencySymbol
  )?.type;
};

const TransferWidgetView: React.FC<{
  sourceCurrency: string;
  sourceNetwork: string;
  sourceAmount: string;
  sourceCurrencySymbol: string;
  availableSourceCurrenciesByNetworks: Record<
    string,
    { symbol: string; type: string }[]
  >;
  availableDestinationCurrenciesByNetworks: Record<
    string,
    { symbol: string; type: string }[]
  >;
  destinationCurrency: string;
  destinationNetwork: string;
  destinationAmount: string;
  destinationCurrencySymbol: string;
  onSourceDestinationChange: (evt: {
    sourceCurrency: string;
    sourceNetwork: string;
    sourceAmount: string;
    destinationCurrency: string;
    destinationNetwork: string;
    destinationAmount: string;
    sourceCurrencySymbol: string;
    destinationCurrencySymbol: string;
    lastTouchAmountFieldId?: 'sourceAmount' | 'destinationAmount';
  }) => void;
  onInitiateTransfer: () => void;
  isLoadingQuote?: boolean;
  fees?: {
    networkFee: string;
    serviceFee: string;
    totalFees: string;
  };
}> = ({
  availableSourceCurrenciesByNetworks,
  availableDestinationCurrenciesByNetworks,
  sourceCurrencySymbol,
  destinationCurrencySymbol,
  isLoadingQuote,
  fees,
  sourceCurrency,
  sourceNetwork,
  sourceAmount,
  destinationCurrency,
  destinationNetwork,
  destinationAmount,
  onInitiateTransfer,
  onSourceDestinationChange,
}) => {
  const [form] = Form.useForm<TransferFormData>();
  const [isSourceSelectOpen, setIsSourceSelectOpen] = useState(false);
  const [isDestinationSelectOpen, setIsDestinationSelectOpen] = useState(false);

  const handleSourceSelectToggle = () => {
    setIsSourceSelectOpen(!isSourceSelectOpen);
  };

  const handleDestinationSelectToggle = () => {
    setIsDestinationSelectOpen(!isDestinationSelectOpen);
  };

  const animationProps = {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.2 },
  };

  useEffect(() => {
    form.setFieldsValue({
      sourceAmount,
      sourceCurrency,
      sourceNetwork,
      sourceCurrencySymbol,
      destinationAmount,
      destinationCurrency,
      destinationNetwork,
      destinationCurrencySymbol,
    });
  }, [
    form,
    sourceAmount,
    sourceCurrency,
    sourceNetwork,
    sourceCurrencySymbol,
    destinationAmount,
    destinationCurrency,
    destinationNetwork,
    destinationCurrencySymbol,
  ]);

  return (
    <div className="p-6 pt-5 w-full dark:bg-slate-800 text-slate-100 text-center rounded-lg shadow-md">
      <Form
        onFinish={onInitiateTransfer}
        onValuesChange={(changedValues, allValues) => {
          Object.keys(changedValues).forEach((fieldName) => {
            const errors = form.getFieldError(fieldName);
            if (errors.length > 0) {
              form.setFields([
                {
                  name: fieldName,
                  errors: [],
                },
              ]);
            }
          });

          onSourceDestinationChange({
            lastTouchAmountFieldId:
              changedValues.sourceAmount || changedValues.destinationAmount
                ? changedValues.sourceAmount
                  ? 'sourceAmount'
                  : 'destinationAmount'
                : undefined,
            ...allValues,
          });
        }}
        requiredMark={false}
        form={form}
        initialValues={{
          sourceAmount,
          sourceCurrency,
          sourceNetwork,
          sourceCurrencySymbol,

          destinationAmount,
          destinationCurrency,
          destinationNetwork,
          destinationCurrencySymbol,
        }}
        layout="vertical">
        <Flex align="flex-end" gap={'large'}>
          <Popover
            placement="bottomRight"
            trigger={'click'}
            arrow={false}
            overlayInnerStyle={{
              backgroundColor: '#18222d',
            }}
            onOpenChange={(flag) => {
              if (!flag) {
                handleSourceSelectToggle();
              }
            }}
            open={isSourceSelectOpen}
            content={
              <CurrencySelect
                currencySymbol={sourceCurrencySymbol}
                network={sourceNetwork}
                currenciesByNetworks={availableSourceCurrenciesByNetworks}
                onSelect={({ currency, network }) => {
                  form.setFieldsValue({
                    sourceNetwork: network,
                    sourceCurrencySymbol: currency,
                    sourceCurrency: getCurrencyTypeByNetworkAndSymbol(
                      availableSourceCurrenciesByNetworks,
                      network,
                      currency
                    ),
                  });
                  handleSourceSelectToggle();
                  onSourceDestinationChange({
                    ...form.getFieldsValue(),
                    sourceNetwork: network,
                    sourceCurrencySymbol: currency,
                    lastTouchAmountFieldId: 'sourceAmount',
                  });
                }}
              />
            }>
            <Item
              name="sourceAmount"
              label={'You Send'}
              validateTrigger={['onSubmit']}
              style={{ margin: 0 }}
              className="flex-1 no-explain-message"
              rules={[{ required: true, message: '' }]}>
              <Input
                type="number"
                step="1"
                min="0"
                onKeyDown={(e) => {
                  if (
                    !/^[0-9]$/.test(e.key) &&
                    e.key !== 'Backspace' &&
                    e.key !== 'Delete' &&
                    e.key !== 'ArrowLeft' &&
                    e.key !== 'ArrowRight'
                  ) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter amount to send"
                className="bg-slate-700 border-slate-600 text-slate-100"
                suffix={
                  <div onClick={handleSourceSelectToggle}>
                    <SelectedCurrencyNetwork
                      currency={sourceCurrencySymbol}
                      network={sourceNetwork}
                    />
                  </div>
                }
              />
            </Item>
          </Popover>
          <div className="relative flex justify-center items-center w-8 h-8 mx-auto ">
            <div className="absolute w-8 h-8 bg-slate-700 rounded-full"></div>
            <ArrowRightIcon className="w-4 z-10 text-slate-300" />
          </div>

          <Popover
            placement="bottomRight"
            trigger={'click'}
            arrow={false}
            overlayInnerStyle={{
              backgroundColor: '#18222d',
            }}
            onOpenChange={(flag) => {
              if (!flag) {
                handleDestinationSelectToggle();
              }
            }}
            open={isDestinationSelectOpen}
            content={
              <CurrencySelect
                currencySymbol={destinationCurrencySymbol}
                network={destinationNetwork}
                currenciesByNetworks={availableDestinationCurrenciesByNetworks}
                onSelect={({ currency, network }) => {
                  form.setFieldsValue({
                    destinationNetwork: network,
                    destinationCurrencySymbol: currency,
                    destinationCurrency: getCurrencyTypeByNetworkAndSymbol(
                      availableDestinationCurrenciesByNetworks,
                      network,
                      currency
                    ),
                  });
                  handleDestinationSelectToggle();
                  onSourceDestinationChange({
                    ...form.getFieldsValue(),
                    destinationNetwork: network,
                    destinationCurrencySymbol: currency,
                    lastTouchAmountFieldId: 'destinationAmount',
                  });
                }}
              />
            }>
            <Item
              name="destinationAmount"
              label={'Recipient Gets'}
              className="flex-1 no-explain-message"
              style={{ margin: 0 }}
              validateTrigger={['onSubmit']}
              rules={[{ required: true, message: '' }]}>
              <Input
                type="number"
                step="1"
                min="0"
                onKeyDown={(e) => {
                  if (
                    !/^[0-9]$/.test(e.key) &&
                    e.key !== 'Backspace' &&
                    e.key !== 'Delete' &&
                    e.key !== 'ArrowLeft' &&
                    e.key !== 'ArrowRight'
                  ) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter amount to receive"
                className="bg-slate-700 border-slate-600 text-slate-100"
                suffix={
                  <div onClick={handleDestinationSelectToggle}>
                    <SelectedCurrencyNetwork
                      currency={destinationCurrencySymbol}
                      network={destinationNetwork}
                    />
                  </div>
                }
              />
            </Item>
          </Popover>

          <Button
            type="primary"
            htmlType="submit"
            className="bg-indigo-600 w-48 hover:!bg-indigo-500 mb-[2px]"
            icon={<ArrowsRightLeftIcon className="w-4" />}>
            Create Transfer
          </Button>
        </Flex>

        <Item name="sourceCurrency" hidden={true}>
          <Input type="hidden" />
        </Item>
        <Item name="sourceNetwork" hidden={true}>
          <Input type="hidden" />
        </Item>
        <Item name="destinationCurrency" hidden={true}>
          <Input type="hidden" />
        </Item>
        <Item name="destinationNetwork" hidden={true}>
          <Input type="hidden" />
        </Item>

        <AnimatePresence>
          {(isLoadingQuote || fees?.totalFees) && (
            <motion.div
              key="content"
              {...animationProps}
              className="overflow-hidden">
              {isLoadingQuote ? (
                <div className="flex items-center pt-6 pb-1 justify-center w-full">
                  <Spin size="large" className="text-indigo-600" />
                </div>
              ) : (
                fees?.totalFees && (
                  <Flex
                    justify="space-between"
                    align="flex-end"
                    className="pt-4">
                    <div className="w-64">
                      <h4 className="text-slate-300 text-sm mb-2 text-left">
                        Transfer Details:
                      </h4>
                      <Flex justify="space-between" className="text-xs mb-1">
                        <span className="text-slate-400">Network Fee</span>
                        <span className="text-slate-400">{`${
                          fees.networkFee
                        } ${sourceCurrencySymbol?.toUpperCase()}`}</span>
                      </Flex>
                      <Flex justify="space-between" className="text-xs mb-1">
                        <span className="text-slate-400">Service Fee</span>
                        <span className="text-slate-400">{`${
                          fees.serviceFee
                        } ${sourceCurrencySymbol?.toUpperCase()}`}</span>
                      </Flex>
                      <Flex
                        justify="space-between"
                        className="text-xs font-semibold">
                        <span className="text-slate-300">Total Fees</span>
                        <span className="text-slate-300">{`${
                          fees.totalFees
                        } ${sourceCurrencySymbol?.toUpperCase()}`}</span>
                      </Flex>
                    </div>
                  </Flex>
                )
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </Form>
    </div>
  );
};

export const InitiateTransferWidget: React.FC<{
  onInitiateTransfer: (candidate: TransferFormData) => void;
}> = ({ onInitiateTransfer }) => {
  const {
    isPending: isSourceCapabilitiesPending,
    getDefaultSource,
    getSourceCurrenciesByNetworks,
  } = useSourceCapabilities();

  const [transferCandidate, setTransferCandidate] = useState<
    TransferFormData & {
      lastTouchAmountFieldId?: 'sourceAmount' | 'destinationAmount';
    }
  >({
    sourceCurrency: undefined,
    sourceCurrencySymbol: undefined,
    sourceNetwork: undefined,
    sourceAmount: undefined,
    destinationCurrency: undefined,
    destinationCurrencySymbol: undefined,
    destinationNetwork: undefined,
    destinationAmount: undefined,
  });

  const [debouncedTransferCandidate] = useDebounce(transferCandidate, 1000);

  const {
    isPending: isDestinationCapabilitiesPending,
    getDefaultDestination,
    getAvailableDestinationCurrenciesByNetworks,
  } = useDestinationCapabilities({
    sourceCurrencySymbol: transferCandidate.sourceCurrencySymbol,
    sourceNetwork: transferCandidate.sourceNetwork,
  });

  const { data, isFetching: isFetchingQuote } = useTransferQuote({
    requestedAmountType: debouncedTransferCandidate.lastTouchAmountFieldId
      ? debouncedTransferCandidate.lastTouchAmountFieldId ===
        'destinationAmount'
        ? 'destination'
        : 'source'
      : undefined,

    amount: debouncedTransferCandidate.lastTouchAmountFieldId
      ? debouncedTransferCandidate.lastTouchAmountFieldId ===
        'destinationAmount'
        ? debouncedTransferCandidate.destinationAmount
        : debouncedTransferCandidate.sourceAmount
      : undefined,

    sourceNetwork: transferCandidate.sourceNetwork,
    sourceCurrencySymbol: transferCandidate.sourceCurrencySymbol,
    destinationCurrencySymbol: transferCandidate.destinationCurrencySymbol,
    destinationNetwork: transferCandidate.destinationNetwork,
  });

  useEffect(() => {
    if (data && data.transferCandidate) {
      setTransferCandidate((prevState) => ({
        ...prevState,
        ...data.transferCandidate,
      }));
    }
  }, [data]);

  useEffect(() => {
    if (!transferCandidate.sourceCurrencySymbol && getDefaultSource()) {
      const { currencySymbol, currency, network } = getDefaultSource();

      setTransferCandidate((prevState) => ({
        ...prevState,
        sourceCurrencySymbol: currencySymbol,
        sourceCurrency: currency,
        sourceNetwork: network,
      }));
    }
  }, [isSourceCapabilitiesPending]);

  useEffect(() => {
    const availableDestinations = getAvailableDestinationCurrenciesByNetworks();
    const isCurrentDestinationAvailable = availableDestinations[
      transferCandidate.destinationNetwork
    ]?.some(
      // @ts-ignore
      (currency) =>
        currency.symbol === transferCandidate.destinationCurrencySymbol
    );

    if (
      !isCurrentDestinationAvailable ||
      !transferCandidate.destinationCurrency
    ) {
      const defaultDestination = getDefaultDestination();
      if (defaultDestination) {
        const { currency, network, currencySymbol } = defaultDestination;
        setTransferCandidate((prevState) => ({
          ...prevState,
          destinationCurrency: currency,
          destinationNetwork: network,
          destinationCurrencySymbol: currencySymbol,
        }));
      }
    }
  }, [
    getAvailableDestinationCurrenciesByNetworks,
    getDefaultDestination,
    transferCandidate.destinationNetwork,
    transferCandidate.destinationCurrencySymbol,
    transferCandidate.destinationCurrency,
  ]);

  if (isSourceCapabilitiesPending || isDestinationCapabilitiesPending) {
    return (
      <div className="p-6 pt-5 w-full dark:bg-slate-800 text-slate-100 text-center rounded-lg shadow-md">
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  return (
    <TransferWidgetView
      availableSourceCurrenciesByNetworks={getSourceCurrenciesByNetworks()}
      availableDestinationCurrenciesByNetworks={getAvailableDestinationCurrenciesByNetworks()}
      sourceCurrency={transferCandidate.sourceCurrency}
      sourceNetwork={transferCandidate.sourceNetwork}
      sourceAmount={transferCandidate.sourceAmount}
      sourceCurrencySymbol={transferCandidate.sourceCurrencySymbol}
      destinationCurrency={transferCandidate.destinationCurrency}
      destinationNetwork={transferCandidate.destinationNetwork}
      destinationAmount={transferCandidate.destinationAmount}
      destinationCurrencySymbol={transferCandidate.destinationCurrencySymbol}
      isLoadingQuote={isFetchingQuote}
      fees={data?.quote}
      onInitiateTransfer={() => {
        onInitiateTransfer(transferCandidate);
      }}
      onSourceDestinationChange={(evt) => {
        setTransferCandidate({
          ...transferCandidate,
          ...evt,
        });
      }}
    />
  );
};
